import type { Persistence } from '../persistence/Persistence';

/**
 * The world clock (HEX-99 / ADR-0018 §5).
 *
 * The overworld is turn-based on the player's feet: **one movement = one tick**.
 * `advance()` is called once per player step (App's `onMoved`), unconditionally —
 * crossing a fresh tile and re-crossing an already-discovered one both tick,
 * because the unit of world time is *movement*, not discovery.
 *
 * SCOPE — per-WORLD, not per-character. The downstream consumer (Evil Pressure /
 * town reversion, Wave 3) is a property of the shared map: towns revert on global
 * ticks-elapsed, so a per-character counter would be incoherent the moment two
 * characters share the world. The count therefore persists world-scoped (the
 * single-row `world_clock` table), never on the `Character`.
 *
 * COUNTED, NEVER WALL-CLOCK (ADR-0013 determinism). `tick` is a pure integer step
 * count — no `Date.now()`. This is deliberately distinct from `sessionBootAt`
 * (encounterRespawn.ts), which is a wall-clock *session-boundary* gate, not a
 * counted clock: the two measure different things — a per-session eligibility
 * boundary (`resolvedAt < sessionBootAt`) versus cumulative movement — and rightly
 * coexist. They are NOT a duplicated clock: folding `sessionBootAt` into this tick
 * would force changing `encounterRespawn`'s `resolvedAt` semantics + persisted
 * representation (a wall-clock ms-epoch stamp), which is out of this ticket's
 * scope. The respawn roll already takes its determinism from `tile.crossings`
 * (per-tile footfall), not from any clock; wiring Evil Pressure et al. onto `tick`
 * is Wave 3.
 *
 * CONSUMERS read `tick`; none are wired yet (Evil Pressure is Wave 3). Pillar
 * fast-travel does NOT advance the clock — whether a hop costs many ticks at once
 * is an open ADR-0019 (§7) question, deferred.
 */
export class WorldClock {
  private count: number;

  constructor(
    initial: number,
    private readonly persistence: Pick<Persistence, 'saveWorldTick'>,
  ) {
    // The clock only ever counts forward; a corrupt/negative/non-finite persisted
    // value floors at 0.
    this.count = Number.isFinite(initial) ? Math.max(0, Math.floor(initial)) : 0;
  }

  /** Load the persisted world tick and bind a clock to it. */
  static async load(
    persistence: Pick<Persistence, 'loadWorldTick' | 'saveWorldTick'>,
  ): Promise<WorldClock> {
    return new WorldClock(await persistence.loadWorldTick(), persistence);
  }

  /** Current world tick — the read API for downstream consumers (Wave 3). */
  get tick(): number {
    return this.count;
  }

  /**
   * Advance the clock by exactly one tick (one player movement) and persist the
   * new value. Returns the new tick. Unconditional by contract: the caller
   * invokes it on every step, re-crossings of already-discovered tiles included.
   */
  async advance(): Promise<number> {
    this.count += 1;
    await this.persistence.saveWorldTick(this.count);
    return this.count;
  }
}
